import { useState, useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { mockFAQ } from '@/mocks/travel';

gsap.registerPlugin(ScrollTrigger);

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const items = sectionRef.current?.querySelectorAll('.faq-item');
      if (items) {
        gsap.fromTo(
          items,
          { y: 30, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 0.5,
            stagger: 0.08,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: sectionRef.current,
              start: 'top 80%',
              toggleActions: 'play none none reverse',
            },
          }
        );
      }
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section ref={sectionRef} className="relative w-full py-24 md:py-32 px-6 md:px-10 lg:px-16 bg-background-100">
      <div className="max-w-[1440px] mx-auto">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-16 md:mb-20">
            <span className="inline-block text-xs tracking-[0.2em] uppercase text-primary-500 font-label mb-4">
              FAQ
            </span>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light text-foreground-900 leading-tight">
              Perguntas <span className="italic">frequentes</span>
            </h2>
          </div>

          <div className="space-y-3">
            {mockFAQ.map((item, index) => (
              <div
                key={index}
                className="faq-item bg-background-50 border border-background-200/60 rounded-xl overflow-hidden transition-all duration-300"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer"
                >
                  <span className="font-heading text-lg md:text-xl font-medium text-foreground-900 pr-4">
                    {item.question}
                  </span>
                  <div className={`w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-full transition-all duration-500 ${
                    openIndex === index
                      ? 'bg-primary-500 text-white rotate-45'
                      : 'bg-background-100 text-foreground-500'
                  }`}>
                    <i className="ri-add-line text-lg"></i>
                  </div>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-500 ease-out ${
                    openIndex === index ? 'max-h-96 pb-6' : 'max-h-0'
                  }`}
                >
                  <div className="px-6 text-foreground-600 leading-relaxed text-sm md:text-base">
                    {item.answer}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}